import { useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Trash2, Webhook } from "lucide-react";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import {
  FormCard,
  FormStackedRow,
  InlineMessage,
  SettingsHeader,
} from "@/components/settings/settings-ui";
import { formatRelativeTime } from "@/utils";
import {
  useCreateWebhookMutation,
  useDeleteWebhookMutation,
  useWorkspaceWebhooksQuery,
  type CreatedWebhook,
  type WorkspaceWebhookRow,
} from "@/data/webhooks";

interface WebhooksSectionProps {
  workspaceSlug?: string | null;
  canManage: boolean;
}

export function WebhooksSection({
  workspaceSlug,
  canManage,
}: WebhooksSectionProps) {
  const webhooksQuery = useWorkspaceWebhooksQuery(workspaceSlug);
  const createMutation = useCreateWebhookMutation(workspaceSlug ?? null);
  const deleteMutation = useDeleteWebhookMutation(workspaceSlug ?? null);
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [created, setCreated] = useState<CreatedWebhook | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Deletion is irreversible (deliveries stop instantly and the delivery
  // history goes with the endpoint), so the button arms on first click and
  // deletes on the second.
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const webhooks = webhooksQuery.data?.webhooks ?? [];

  async function createEndpoint() {
    const trimmedUrl = url.trim();
    if (!workspaceSlug || !trimmedUrl) return;
    setBusyId("create");
    setError(null);
    try {
      const result = await createMutation.mutateAsync({
        url: trimmedUrl,
        description: description.trim() || undefined,
      });
      setCreated(result);
      setUrl("");
      setDescription("");
      toast.success("Webhook created");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error("Failed to create webhook");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteEndpoint(webhook: WorkspaceWebhookRow) {
    setBusyId(webhook.id);
    setConfirmId(null);
    setError(null);
    try {
      await deleteMutation.mutateAsync(webhook.id);
      if (created?.webhook.id === webhook.id) setCreated(null);
      toast.success("Webhook deleted");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error("Failed to delete webhook");
    } finally {
      setBusyId(null);
    }
  }

  async function copySecret(secret: string) {
    try {
      await navigator.clipboard.writeText(secret);
      toast.success("Signing secret copied");
    } catch {
      toast.error("Copy failed — select the secret text and copy manually");
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <SettingsHeader
        title="Webhooks"
        description="Each processed capture is POSTed to your endpoint as a signed capture.processed event, with retries. New webhooks start from now — history stays available via the pull API."
      />

      <div className="space-y-6">
        {error ? <InlineMessage variant="error">{error}</InlineMessage> : null}

        {canManage ? (
          <FormCard>
            <FormStackedRow
              label="Add endpoint"
              hint="Your endpoint receives every processed capture in this workspace. The signing secret is shown once, right after the webhook is created."
            >
              <div className="grid gap-2">
                <Input
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                  placeholder="https://example.com/parable/webhook"
                />
                <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                  <Input
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void createEndpoint();
                      }
                    }}
                    placeholder="What's this endpoint for? (optional)"
                  />
                  <Button
                    type="button"
                    disabled={busyId === "create" || !url.trim()}
                    onClick={() => void createEndpoint()}
                  >
                    {busyId === "create" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Webhook className="h-3.5 w-3.5" />
                    )}
                    Add endpoint
                  </Button>
                </div>
              </div>
            </FormStackedRow>
          </FormCard>
        ) : (
          <InlineMessage variant="info">
            Only workspace admins can create or delete webhooks.
          </InlineMessage>
        )}

        {created ? (
          <InlineMessage variant="success">
            <div className="space-y-2">
              <div>
                Copy the signing secret for{" "}
                <strong>{created.webhook.url}</strong> now — it won't be shown
                again. Use it to verify the X-Parable-Signature header on every
                delivery.
              </div>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-background px-2.5 py-1.5 font-mono text-[12px] text-foreground">
                  {created.secret}
                </code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void copySecret(created.secret)}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </Button>
              </div>
            </div>
          </InlineMessage>
        ) : null}

        <FormCard>
          <FormStackedRow label="Endpoints">
            {webhooksQuery.isPending && workspaceSlug ? (
              <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading webhooks
              </div>
            ) : webhooks.length === 0 ? (
              <div className="border-t border-border py-3 text-[13px] text-muted-foreground">
                No webhooks yet.
              </div>
            ) : (
              <div className="divide-y divide-border border-t border-border">
                {webhooks.map((webhook) => (
                  <WebhookRow
                    key={webhook.id}
                    webhook={webhook}
                    busy={busyId === webhook.id}
                    armed={confirmId === webhook.id}
                    canManage={canManage}
                    onArm={(id) => setConfirmId(id)}
                    onDelete={deleteEndpoint}
                  />
                ))}
              </div>
            )}
          </FormStackedRow>
        </FormCard>
      </div>
    </div>
  );
}

function WebhookRow({
  webhook,
  busy,
  armed,
  canManage,
  onArm,
  onDelete,
}: {
  webhook: WorkspaceWebhookRow;
  busy: boolean;
  armed: boolean;
  canManage: boolean;
  onArm: (id: string | null) => void;
  onDelete: (webhook: WorkspaceWebhookRow) => Promise<void>;
}) {
  return (
    <div className="grid items-center gap-3 py-3 md:grid-cols-[minmax(0,1fr)_auto]">
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium text-foreground">
          {webhook.description || webhook.url}
        </div>
        <div className="truncate text-[12px] text-muted-foreground">
          {webhook.description ? (
            <>
              <span className="font-mono">{webhook.url}</span> ·{" "}
            </>
          ) : null}
          created {formatWebhookDate(webhook.created_at)} · last delivery{" "}
          {formatLastDelivered(webhook.last_delivered_at)}
        </div>
      </div>
      {canManage ? (
        <Button
          type="button"
          size="sm"
          variant={armed ? "destructive" : "outline"}
          disabled={busy}
          onClick={() => (armed ? void onDelete(webhook) : onArm(webhook.id))}
          onBlur={() => armed && onArm(null)}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
          {armed ? "Confirm delete" : "Delete"}
        </Button>
      ) : (
        <div />
      )}
    </div>
  );
}

function formatWebhookDate(value: string | number): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "recently";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLastDelivered(value: string | number | null): string {
  if (value == null) return "never";
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return "never";
  return formatRelativeTime(ms);
}
